import { useState, useEffect, useMemo } from 'react';
import { Plus, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useDeviceMutation } from '@/hooks/useDeviceMutation';
import { SectionHeader } from './SectionHeader';
import type { DeviceStatus, ModuleEntry } from '@/lib/types';

interface Props { device: DeviceStatus; disabled: boolean }

interface DraftModule { id: string; slaveId: number; channels: 8 | 16 | 32; startRelay: number }

const BAUDS = [9600, 19200, 38400, 57600, 115200] as const;

function nextLetter(n: number) {
  return String.fromCharCode(65 + (n % 26));
}

function validate(rows: DraftModule[]): string | null {
  for (let i = 0; i < rows.length; i++) {
    const a = rows[i]!;
    if (a.startRelay < 9) return `Module ${a.id}: start relay must be ≥ 9`;
    const aEnd = a.startRelay + a.channels - 1;
    if (aEnd > 50) return `Module ${a.id}: end relay ${aEnd} exceeds max 50`;
    for (let j = i + 1; j < rows.length; j++) {
      const b = rows[j]!;
      const bEnd = b.startRelay + b.channels - 1;
      if (a.startRelay <= bEnd && b.startRelay <= aEnd) return `Modules ${a.id} and ${b.id} overlap`;
    }
  }
  const ids = rows.map((r) => r.slaveId);
  if (new Set(ids).size !== ids.length) return 'Duplicate slave IDs found';
  return null;
}

export function ModulesSection({ device, disabled }: Props) {
  const initial = device.modules?.modules ?? [];
  const [baud, setBaud] = useState<number>(device.modules?.modbusBaud ?? 9600);
  const [rows, setRows] = useState<DraftModule[]>(initial.map((m: ModuleEntry) => ({ ...m })));

  useEffect(() => {
    if (device.modules) {
      setBaud(device.modules.modbusBaud ?? 9600);
      setRows(device.modules.modules.map((m) => ({ ...m })));
    }
  }, [device.modules]);

  const error = useMemo(() => validate(rows), [rows]);

  const mut = useDeviceMutation<{ modbusBaud: number; modules: DraftModule[] }>(
    device.client_id, 'modules', { successToast: 'Modbus modules saved' },
  );

  const addRow = () => {
    setRows((r) => [...r, { id: nextLetter(r.length), slaveId: r.length + 1, channels: 16, startRelay: 9 }]);
  };

  const updateRow = (i: number, patch: Partial<DraftModule>) => {
    setRows((r) => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  };

  const removeRow = (i: number) => setRows((r) => r.filter((_, idx) => idx !== i));

  const submit = () => {
    if (error) return;
    mut.mutate({ modbusBaud: baud, modules: rows });
  };

  return (
    <div className="space-y-5">
      <SectionHeader title="Modbus modules"
        description="Each external relay module connected over RS-485. Relay numbers above 8 are routed to these modules." />

      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold text-slate-700">Baud rate</span>
        <select
          value={baud} disabled={disabled}
          onChange={(e) => setBaud(parseInt(e.target.value, 10))}
          className="h-9 px-3 rounded-lg border border-slate-300 bg-white text-sm"
        >
          {BAUDS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>

      {error && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">ID</th>
              <th className="text-left px-3 py-2 font-semibold">Slave</th>
              <th className="text-left px-3 py-2 font-semibold">Channels</th>
              <th className="text-left px-3 py-2 font-semibold">Start relay</th>
              <th className="text-left px-3 py-2 font-semibold">End</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-slate-400 text-xs">No modules yet — click + Add module</td></tr>
            )}
            {rows.map((m, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="p-1.5"><Input value={m.id} disabled={disabled} maxLength={8}
                  className="!h-8 !text-xs" onChange={(e) => updateRow(i, { id: e.target.value })} /></td>
                <td className="p-1.5"><Input type="number" min={1} max={247} value={m.slaveId}
                  disabled={disabled} className="!h-8 !text-xs"
                  onChange={(e) => updateRow(i, { slaveId: parseInt(e.target.value || '0', 10) })} /></td>
                <td className="p-1.5">
                  <select value={m.channels} disabled={disabled}
                    onChange={(e) => updateRow(i, { channels: parseInt(e.target.value, 10) as 8 | 16 | 32 })}
                    className="h-8 w-full px-2 rounded border border-slate-300 bg-white text-xs">
                    <option value={8}>8</option>
                    <option value={16}>16</option>
                    <option value={32}>32</option>
                  </select>
                </td>
                <td className="p-1.5"><Input type="number" min={9} max={50} value={m.startRelay}
                  disabled={disabled} className="!h-8 !text-xs"
                  onChange={(e) => updateRow(i, { startRelay: parseInt(e.target.value || '9', 10) })} /></td>
                <td className="p-1.5 text-xs tabular-nums text-slate-600">{m.startRelay + m.channels - 1}</td>
                <td className="p-1.5">
                  <button disabled={disabled} onClick={() => removeRow(i)}
                    className="p-1 rounded hover:bg-red-50 text-red-500 disabled:opacity-30 disabled:hover:bg-transparent">
                    <X className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={addRow} disabled={disabled}>
          <Plus className="w-4 h-4" /> Add module
        </Button>
        <Button onClick={submit} loading={mut.isPending}
          disabled={disabled || !!error || mut.isPending} className="ml-auto">
          Save modules
        </Button>
      </div>
    </div>
  );
}
